import type { LngLat } from "@/utils/polyline";

/**
 * Device-local "hearted" places. Purely private — these never leave the
 * device and are NOT shared with other users (unlike public tags/ratings).
 * Think of it as a personal favorites list.
 */
export interface HeartedPlace {
  id: string;
  coords: LngLat;
  /** Optional human label captured at heart-time (e.g. tag chosen, address). */
  note?: string;
  createdAt: number;
}

const KEY = "pleasant.heartedPlaces.v1";

function read(): HeartedPlace[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (p): p is HeartedPlace =>
        p && typeof p.id === "string" &&
        Array.isArray(p.coords) && p.coords.length === 2,
    );
  } catch {
    return [];
  }
}

function write(places: HeartedPlace[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(places));
  } catch { /* quota / private mode */ }
  window.dispatchEvent(new CustomEvent("hearted-places-changed"));
}

export function loadHeartedPlaces(): HeartedPlace[] {
  return read();
}

/** Coord-distance helper (meters). */
function distM(a: LngLat, b: LngLat): number {
  const dLat = (a[1] - b[1]) * 111_000;
  const dLng = (a[0] - b[0]) * 111_000 * Math.cos((a[1] * Math.PI) / 180);
  return Math.hypot(dLat, dLng);
}

export function findHeartedNear(coords: LngLat, toleranceM = 30): HeartedPlace | undefined {
  for (const p of read()) {
    if (distM(p.coords, coords) < toleranceM) return p;
  }
  return undefined;
}

export function isHearted(coords: LngLat, toleranceM = 30): boolean {
  return !!findHeartedNear(coords, toleranceM);
}

/** Toggle heart at the given coords. Returns the new state. */
export function toggleHeart(coords: LngLat, note?: string, toleranceM = 30): boolean {
  const all = read();
  const idx = all.findIndex((p) => distM(p.coords, coords) < toleranceM);
  if (idx >= 0) {
    all.splice(idx, 1);
    write(all);
    return false;
  }
  all.unshift({
    id: crypto.randomUUID(),
    coords,
    note: note?.trim() || undefined,
    createdAt: Date.now(),
  });
  write(all.slice(0, 200));
  return true;
}
